/**
 * SHOP SECTION
 * Browse and purchase items from the TISA shop
 */

import { useState } from 'react';
import { SHOP_ITEMS } from '@/config/tisa-settings';
import type { ShopItem } from '@/config/tisa-settings';
import { ShopItemCard } from './ShopItemCard';
import { Filter, Search } from 'lucide-react';

interface ShopSectionProps {
  vaultBalance: number;
  studentLevel: number;
  onPurchase: (item: ShopItem) => void;
}

type Category = 'all' | 'supplies' | 'privileges' | 'events' | 'customization' | 'consumables';
type Rarity = 'all' | 'common' | 'rare' | 'epic' | 'legendary';

export function ShopSection({ vaultBalance, studentLevel, onPurchase }: ShopSectionProps) {
  const [selectedCategory, setSelectedCategory] = useState<Category>('all');
  const [selectedRarity, setSelectedRarity] = useState<Rarity>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Filter items
  const filteredItems = SHOP_ITEMS.filter(item => {
    // Category filter
    if (selectedCategory !== 'all' && item.category !== selectedCategory) return false;

    // Rarity filter
    if (selectedRarity !== 'all' && item.rarity !== selectedRarity) return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = item.name.toLowerCase().includes(query);
      const matchesDescription = item.description.toLowerCase().includes(query);
      if (!matchesName && !matchesDescription) return false;
    }

    return true;
  });

  const categories: { value: Category; label: string; emoji: string }[] = [
    { value: 'all', label: 'All Items', emoji: '🛍️' },
    { value: 'supplies', label: 'Supplies', emoji: '📚' },
    { value: 'privileges', label: 'Privileges', emoji: '⭐' },
    { value: 'events', label: 'Events', emoji: '🎉' },
    { value: 'customization', label: 'Customization', emoji: '🎨' },
    { value: 'consumables', label: 'Consumables', emoji: '🍎' },
  ];

  const rarities: { value: Rarity; label: string; color: string }[] = [
    { value: 'all', label: 'All Rarities', color: 'bg-gray-500' },
    { value: 'common', label: 'Common', color: 'bg-gray-500' },
    { value: 'rare', label: 'Rare', color: 'bg-blue-500' },
    { value: 'epic', label: 'Epic', color: 'bg-purple-500' },
    { value: 'legendary', label: 'Legendary', color: 'bg-yellow-500' },
  ];

  return (
    <div className="space-y-6">
      {/* Header - Luna Branding: "The Bazaar" */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <div className="text-4xl">🛍️</div>
          <h2 className="text-3xl font-bold text-white font-child-display">
            The Bazaar
          </h2>
        </div>
        <p className="text-purple-100">
          Spend your hard-earned coins on awesome items and rewards! 🪙
        </p>
        <div className="mt-4 bg-white/20 backdrop-blur-sm rounded-lg p-3 flex items-center gap-2">
          <span className="text-white font-semibold">Your Balance:</span>
          <span className="text-2xl font-bold text-yellow-300">
            {vaultBalance} 🪙
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-md space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search items..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none"
          />
        </div>

        {/* Category Filter */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-semibold text-gray-700">Category:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                className={`
                  px-4 py-2 rounded-lg font-semibold transition-all
                  ${selectedCategory === cat.value
                    ? 'bg-purple-600 text-white shadow-lg scale-105'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                {cat.emoji} {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Rarity Filter */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-semibold text-gray-700">Rarity:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {rarities.map(rar => (
              <button
                key={rar.value}
                onClick={() => setSelectedRarity(rar.value)}
                className={`
                  px-4 py-2 rounded-lg font-semibold text-white transition-all flex items-center gap-2
                  ${selectedRarity === rar.value
                    ? `${rar.color} shadow-lg scale-105`
                    : `${rar.color} opacity-60 hover:opacity-100`
                  }
                `}
              >
                {rar.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="text-sm text-gray-600">
          Showing <span className="font-bold text-purple-600">{filteredItems.length}</span> items
        </div>
      </div>

      {/* Shop Items Grid - Auto-fill to maximize columns, min 188px */}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(188px, 1fr))' }}>
        {filteredItems.map(item => (
          <ShopItemCard
            key={item.id}
            item={item}
            vaultBalance={vaultBalance}
            studentLevel={studentLevel}
            onPurchase={onPurchase}
          />
        ))}
      </div>

      {/* Empty State */}
      {filteredItems.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl">
          <div className="text-6xl mb-4">🔍</div>
          <p className="text-xl font-semibold text-gray-700">No items found</p>
          <p className="text-gray-500 mt-2">Try adjusting your filters</p>
        </div>
      )}
    </div>
  );
}
